import {
  AsyncResp,
  PaginatedResp,
  requestPapi,
  RequestParams,
} from '@paradigm/api/src/fetch-api';
import { getQueryString } from '@paradigm/api/src/internal/utils';
import { Bbo } from '../entities/drfq/bbo';
import { Counterparty, Drfq } from '../entities/drfq/domain';
import { Instrument } from '../entities/drfq/instruments';
import { Order } from '../entities/drfq/orders';
import { Trade, TradeTapeItem } from '../entities/drfq/trades';
import {
  processDrfq,
  processDrfqBbo,
  processDrfqInstrument,
  processDrfqOrder,
  processDrfqTrade,
  processDrfqTradeTape,
} from './adapters/drfq-api';
import {
  CancelAllOrdersReq,
  CancelAllOrdersResp,
  CreateOrderReq,
  CreateRfqReq,
  InstrumentIdReq,
  ListCounterpartiesReq,
  ListInstrumentsReq,
  ListOrdersReq,
  ListRfqsReq,
  ListTradesReq,
  ListTradeTapeReq,
  OrderIdReq,
  PricingReq,
  PricingResp,
  RawBbo,
  RawDrfq,
  RawInstrument,
  RawOrder,
  RawTrade,
  RawTradeTapeItem,
  RfqIdReq,
  RfqOrdersResp,
  TradeIdReq,
  ReplaceOrderReq,
  CreateOrReplaceOrder,
} from './drfq-types';

const BASE_URL = '/v2/drfq';

async function requestDrfqV2<T>(req: RequestParams): AsyncResp<T> {
  return requestPapi({
    ...req,
    url: `${BASE_URL}${req.url}`,
  });
}

export async function listRfqs(
  req: ListRfqsReq,
): AsyncResp<PaginatedResp<Drfq>> {
  const {
    cursor,
    pageSize,
    productCodes,
    strategyCodes,
    role,
    state,
    baseCurrency,
    venue,
    counterparties,
    ...rest
  } = req;

  const query = getQueryString([
    ['cursor', cursor],
    ['page_size', pageSize],
    ['product_codes', productCodes],
    ['strategies', strategyCodes],
    ['role', role],
    ['state', state],
    ['base_currency', baseCurrency],
    ['venue', venue],
    ['ticker', counterparties],
  ]);

  const resp = await requestDrfqV2<PaginatedResp<RawDrfq>>({
    ...rest,
    method: 'GET',
    url: `/rfqs${query}`,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: {
      ...resp.data,
      results: resp.data.results.map(processDrfq),
    },
  };
}

export async function getRfq(req: RfqIdReq): AsyncResp<Drfq> {
  const { rfqId, signal } = req;
  const resp = await requestDrfqV2<RawDrfq>({
    signal,
    method: 'GET',
    url: `/rfqs/${rfqId}`,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: processDrfq(resp.data),
  };
}

export async function cancelRfq(req: RfqIdReq): AsyncResp<null> {
  const { rfqId, signal } = req;
  const resp = await requestDrfqV2<null>({
    signal,
    method: 'DELETE',
    url: `/rfqs/${rfqId}`,
  });

  return resp;
}

export async function createRfq(req: CreateRfqReq): AsyncResp<Drfq> {
  const { signal, ...body } = req;
  const resp = await requestDrfqV2<RawDrfq>({
    signal,
    method: 'POST',
    url: '/rfqs',
    body,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: processDrfq(resp.data),
  };
}

export async function listTradeTape(
  req: ListTradeTapeReq,
): AsyncResp<PaginatedResp<TradeTapeItem>> {
  const {
    cursor,
    pageSize,
    rfqId,
    kind,
    venue,
    productCodes,
    strategyCodes,
    ...rest
  } = req;

  const query = getQueryString([
    ['cursor', cursor],
    ['page_size', pageSize],
    ['rfq_id', rfqId],
    ['kind', kind],
    ['venue', venue],
    ['product_codes', productCodes],
    ['strategies', strategyCodes],
  ]);

  const resp = await requestDrfqV2<PaginatedResp<RawTradeTapeItem>>({
    ...rest,
    method: 'GET',
    url: `/trade_tape${query}`,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: {
      ...resp.data,
      results: resp.data.results.map(processDrfqTradeTape),
    },
  };
}

export async function listTrades(
  req: ListTradesReq,
): AsyncResp<PaginatedResp<Trade>> {
  const { cursor, pageSize, rfqId, orderId, role, state, venue, ...rest } = req;

  const query = getQueryString([
    ['cursor', cursor],
    ['page_size', pageSize],
    ['rfq_id', rfqId],
    ['order_id', orderId],
    ['role', role],
    ['state', state],
    ['venue', venue],
  ]);

  const resp = await requestDrfqV2<PaginatedResp<RawTrade>>({
    ...rest,
    method: 'GET',
    url: `/trades${query}`,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: {
      ...resp.data,
      results: resp.data.results.map(processDrfqTrade),
    },
  };
}

export async function getTrade(req: TradeIdReq): AsyncResp<Trade> {
  const { tradeId, signal } = req;
  const resp = await requestDrfqV2<RawTrade>({
    signal,
    method: 'GET',
    url: `/trades/${tradeId}`,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: processDrfqTrade(resp.data),
  };
}

export async function listOrders(
  req: ListOrdersReq,
): AsyncResp<PaginatedResp<Order>> {
  const { cursor, rfqId, pageSize, state, baseCurrency, venue, ...rest } = req;

  const query = getQueryString([
    ['cursor', cursor],
    ['rfq_id', rfqId],
    ['page_size', pageSize],
    ['state', state],
    ['base_currency', baseCurrency],
    ['venue', venue],
  ]);

  const resp = await requestDrfqV2<PaginatedResp<RawOrder>>({
    ...rest,
    method: 'GET',
    url: `/orders${query}`,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: {
      ...resp.data,
      results: resp.data.results.map(processDrfqOrder),
    },
  };
}

export async function getOrder(req: OrderIdReq): AsyncResp<Order> {
  const { orderId, signal } = req;
  const resp = await requestDrfqV2<RawOrder>({
    signal,
    method: 'GET',
    url: `/orders/${orderId}`,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: processDrfqOrder(resp.data),
  };
}

export async function getRfqOrders(req: RfqIdReq): AsyncResp<RfqOrdersResp> {
  const { rfqId, signal } = req;
  const resp = await requestDrfqV2<RfqOrdersResp>({
    signal,
    method: 'GET',
    url: `/rfqs/${rfqId}/orders`,
  });

  return resp;
}

export async function cancelOrder(req: OrderIdReq): AsyncResp<null> {
  const { orderId, signal } = req;
  const resp = await requestDrfqV2<null>({
    signal,
    method: 'DELETE',
    url: `/orders/${orderId}`,
  });

  return resp;
}

export async function cancelAllOrders(
  req: CancelAllOrdersReq,
): AsyncResp<CancelAllOrdersResp> {
  const { rfqId, side, label, ...rest } = req;

  const query = getQueryString([
    ['rfq_id', rfqId],
    ['side', side],
    ['label', label],
  ]);

  const resp = await requestDrfqV2<CancelAllOrdersResp>({
    ...rest,
    method: 'DELETE',
    url: `/orders${query}`,
  });

  return resp;
}

export async function createOrder(req: CreateOrderReq): AsyncResp<Order> {
  const { signal, ...body } = req;
  const resp = await requestDrfqV2<RawOrder>({
    signal,
    method: 'POST',
    url: '/orders',
    body,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: processDrfqOrder(resp.data),
  };
}

export async function replaceOrder(req: ReplaceOrderReq): AsyncResp<Order> {
  const { orderId, order, ...rest } = req;
  const resp = await requestDrfqV2<RawOrder>({
    ...rest,
    method: 'PUT',
    url: `/orders/${orderId}`,
    body: order,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: processDrfqOrder(resp.data),
  };
}

export async function createOrReplaceOrder(
  req: CreateOrReplaceOrder,
): AsyncResp<Order> {
  if (req.orderId !== null) {
    return replaceOrder(req as ReplaceOrderReq);
  }
  return createOrder(req.order as CreateOrderReq);
}

export async function getRfqBbo(req: RfqIdReq): AsyncResp<Bbo> {
  const { rfqId, ...rest } = req;
  const resp = await requestDrfqV2<RawBbo>({
    ...rest,
    method: 'GET',
    url: `/rfqs/${rfqId}/bbo`,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: processDrfqBbo(resp.data),
  };
}

export async function listInstruments(
  req: ListInstrumentsReq,
): AsyncResp<PaginatedResp<Instrument>> {
  const { signal, ...params } = req;

  const query = getQueryString([
    ['cursor', params.cursor],
    ['page_size', params.pageSize],
    ['state', params.state],
    ['venue', params.venue],
    ['kind', params.kind],
    ['base_currency', params.baseCurrency],
    ['venue_instrument_name', params.venueInstrumentName],
    ['margin_kind', params.marginKind],
    ['include_greeks', params.includeGreeks],
  ]);

  const resp = await requestDrfqV2<PaginatedResp<RawInstrument>>({
    signal,
    method: 'GET',
    url: `/instruments${query}`,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: {
      ...resp.data,
      results: resp.data.results.map(processDrfqInstrument),
    },
  };
}

export async function getInstrument(
  req: InstrumentIdReq,
): AsyncResp<Instrument> {
  const { instrumentId, signal } = req;
  const resp = await requestDrfqV2<RawInstrument>({
    signal,
    method: 'GET',
    url: `/instruments/${instrumentId}`,
  });

  if (!resp.ok) return resp;

  return {
    ...resp,
    data: processDrfqInstrument(resp.data),
  };
}

export async function getCounterparties(
  req: ListCounterpartiesReq,
): AsyncResp<PaginatedResp<Counterparty>> {
  const { group, venues, cursor, pageSize, ...rest } = req;

  const query = getQueryString([
    ['page_size', pageSize],
    ['cursor', cursor],
    ['group', group],
    ['venues', venues != null && venues.length > 0 ? venues.join(',') : null],
  ]);

  const resp = await requestDrfqV2<PaginatedResp<Counterparty>>({
    ...rest,
    method: 'GET',
    url: `/counterparties/${query}`,
  });

  return resp;
}

export async function getPricing(req: PricingReq): AsyncResp<PricingResp> {
  const { signal, body, ...params } = req;

  const resp = await requestDrfqV2<PricingResp>({
    signal,
    method: 'POST',
    url: `/pricing`,
    body: params,
  });

  return resp;
}
